import { baseApi } from '@/rtkQuery/features/services/baseApi';
import type {
    CouponRequest,
    CouponResponse,
    PaymentInformationRequest,
    PaymentInformationResponse,
    SavedCard,
    SavedCardResponse,
} from '@/rtkQuery/features/models/checkout.model';
import type { CheckoutReviewResponse } from '@/rtkQuery/features/models/checkoutReview.model';
import { buildQuery } from '@/rtkQuery/lib';
import { CHECKOUT_ENDPOINTS } from '@/rtkQuery/config/checkoutApi.config';

export const checkoutApi = baseApi.injectEndpoints({
    endpoints: (builder) => ({
        postPaymentInformation: builder.mutation<PaymentInformationResponse, PaymentInformationRequest>({
            query: buildQuery(CHECKOUT_ENDPOINTS.POST_PAYMENT_INFORMATION, 'POST'),
            transformErrorResponse: (response: any, meta) => {
                const errorMessage =
                    response?.data?.Message ||
                    response?.Message ||
                    response?.data?.message ||
                    'Failed to submit payment information';
                (meta as any).error = errorMessage;

                return {
                    status: response.status,
                    message: errorMessage,
                    errors: response.data?.errors || {},
                };
            },
            invalidatesTags: ['Payment', 'SavedCard'],
        }),
        getSavedCard: builder.query<SavedCard | null, void>({
            query: buildQuery(CHECKOUT_ENDPOINTS.GET_SAVED_CARD, 'GET'),
            transformResponse: (response: SavedCardResponse) => {
                if (response?.Result === 'Success') {
                    return response.Object?.Card ?? null;
                }
                throw new Error(response.Message || 'Failed to fetch card');
            },
            transformErrorResponse: (response: any, meta) => {
                const errorMessage =
                    response?.Message || 'Failed to fetch saved card information';
                if (meta) (meta as any).error = errorMessage;
                return {
                    status: response?.status || 500,
                    message: errorMessage,
                };
            },
            providesTags: ['SavedCard'],
        }),

        postApplyCoupon: builder.mutation<CouponResponse, CouponRequest & { productId: string }>({
            query: ({ coupon_code, productId }) => ({
                url: `checkout/applyCoupon?productId=${productId}`,
                method: 'POST',
                body: { coupon_code },
            }),
            transformErrorResponse: (response: any, meta) => {
                const errorMessage =
                    response?.data?.Message ||
                    response?.Message ||
                    response?.data?.message ||
                    'Failed to apply coupon';
                (meta as any).error = errorMessage;

                return {
                    status: response.status,
                    message: errorMessage,
                    errors: response.data?.errors || {},
                };
            },
            invalidatesTags: ['RecentOrders', 'Cart'],
        }),

        submitPayment: builder.mutation<any, void>({
            query: () => ({
                url: 'checkout/submitPayment',
                method: 'POST',
            }),
            transformErrorResponse: (response: any, meta) => {
                const errorMessage =
                    response?.data?.Status ||
                    response?.data?.Message ||
                    response?.Message ||
                    response?.data?.message ||
                    'Failed to submit payment';
                (meta as any).error = errorMessage;

                return {
                    status: response.status,
                    message: errorMessage,
                };
            },
            invalidatesTags: ['RecentOrders', 'WatchedVideos', 'Cart'],
        }),

        getRecentOrders: builder.query<any, void>({
            query: () => ({
                url: 'orders/recent',
                method: 'GET',
            }),
            transformResponse: (response: any) => response,
            transformErrorResponse: (response: any, meta) => {
                const errorMessage =
                    response?.data?.Message ||
                    response?.Message ||
                    response?.data?.message ||
                    'Failed to fetch recent orders';
                (meta as any).error = errorMessage;

                return {
                    status: response.status,
                    message: errorMessage,
                };
            },
            providesTags: ['RecentOrders'],
        }),

        /** ---------------- New GET_REVIEW endpoint ---------------- */
        getReview: builder.query<CheckoutReviewResponse, void>({
            query: buildQuery(CHECKOUT_ENDPOINTS.GET_REVIEW, 'GET'),
            transformResponse: (response: CheckoutReviewResponse) => response,
            transformErrorResponse: (response: any, meta) => {
                const errorMessage =
                    response?.data?.Message ||
                    response?.Message ||
                    response?.data?.message ||
                    'Failed to fetch checkout review.';
                if (meta) (meta as any).error = errorMessage;
                return {
                    status: response?.status || 500,
                    message: errorMessage,
                };
            },
            providesTags: ['Cart', 'Payment', 'CheckoutReview'],
        }),


    }),
});

export const {
    usePostPaymentInformationMutation,
    useGetSavedCardQuery,
    usePostApplyCouponMutation,
    useSubmitPaymentMutation,
    useGetRecentOrdersQuery,
    useGetReviewQuery
} = checkoutApi;
